import React from 'react';
import { CartesianGrid, Line, LineChart, XAxis } from 'recharts';

import { Trend, TrendValue } from '../trend-value';

import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { formatNumber } from '@/lib/formatNumber';
import { useChartDateFormatters } from '@/lib/useChartDateFormatters';
import { cn } from '@/lib/utils';

export interface ArticlesUpdatedProps {
  total: number;
  trend: Trend;
  trendValue: string;
  data: Array<Record<string, number | string | Date>>;
  chartConfig: ChartConfig;
  dashedKeys?: string[];
  locale?: string;
  className?: string;
}

const formatWhole = (value: number) => formatNumber(value, {});

export function ArticlesUpdated({
  total,
  trend,
  trendValue,
  data,
  chartConfig,
  dashedKeys = [],
  locale,
  className,
}: ArticlesUpdatedProps) {
  const { tickFormatter, formatXLabel } = useChartDateFormatters(locale);
  const lineKeys = Object.keys(chartConfig);

  return (
    <Card className={cn('shadow-none border border-dark-300 rounded-lg', className)}>
      <CardHeader className="p-0 space-y-0 border-b border-dark-300">
        <div className="flex flex-col gap-1 justify-center py-4 px-4">
          <p className="uppercase heading-xxxs font-semibold text-dark tracking-normal">Articles updated</p>
          <div className="flex items-center gap-2">
            <span className="font-mono text-3xl leading-10 text-dark">{formatWhole(total)}</span>
            <TrendValue trend={trend} trendValue={trendValue} />
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-4">
        <ChartContainer config={chartConfig} className="h-48 w-full">
          <LineChart data={data} margin={{ top: 8, left: 20, right: 20, bottom: 0 }}>
            <CartesianGrid horizontal vertical={false} className="stroke-dark-300" />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              padding={{ left: 15, right: 15 }}
              minTickGap={32}
              interval="preserveStartEnd"
              tickFormatter={tickFormatter}
              tick={({ x, y, payload, index }) => {
                const isFirst = index === 0;
                const isLast = index === data.length - 1;
                const anchor = isFirst ? 'start' : isLast ? 'end' : 'middle';
                const tickX = isFirst ? x - 15 : isLast ? x + 15 : x;

                return (
                  <text x={tickX} y={y} dy={12} textAnchor={anchor} className="text-xs fill-dark-600">
                    {tickFormatter(payload.value)}
                  </text>
                );
              }}
            />
            <ChartTooltip
              cursor={false}
              content={({ active, payload, label }) => (
                <ChartTooltipContent
                  active={active}
                  payload={payload}
                  label={formatXLabel(label)}
                  className="min-w-52"
                />
              )}
            />
            {lineKeys.map((key) => {
              const isDashed = dashedKeys.includes(key);

              return (
                <Line
                  key={key}
                  dataKey={key}
                  type="monotone"
                  stroke={`var(--color-${key})`}
                  strokeWidth={2}
                  strokeDasharray={isDashed ? '5 5' : undefined}
                  dot={({ cx, cy, stroke }: { cx: number; cy: number; stroke: string }) => (
                    <circle cx={cx} cy={cy} r={4} fill="white" stroke={stroke} strokeWidth={2} />
                  )}
                  activeDot={{ r: 5, strokeWidth: 0 }}
                />
              );
            })}
            <ChartLegend content={<ChartLegendContent />} />
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
